import { useEffect, useRef, useState } from "react";

interface WakeLockSentinel extends EventTarget {
  released: boolean;
  release: () => Promise<void>;
}

type WakeLockNavigator = Navigator & {
  wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinel> };
};

/**
 * Holds a screen wake lock while `enabled` is true so the device does not dim
 * or lock. Requires a secure context; unsupported browsers get `false` back.
 */
export function useWakeLock(enabled: boolean) {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const wakeLock = (navigator as WakeLockNavigator).wakeLock;
    if (!enabled || !wakeLock) return;

    let cancelled = false;

    const acquire = async () => {
      if (cancelled || sentinelRef.current) return;
      if (document.visibilityState !== "visible") return;

      try {
        const sentinel = await wakeLock.request("screen");
        if (cancelled) {
          await sentinel.release();
          return;
        }
        sentinelRef.current = sentinel;
        setIsActive(true);
        sentinel.addEventListener("release", () => {
          sentinelRef.current = null;
          setIsActive(false);
        });
      } catch (error) {
        console.warn("Could not acquire screen wake lock:", error);
        setIsActive(false);
      }
    };

    // The browser drops the lock whenever the tab is hidden, so take it again.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") acquire();
    };

    acquire();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      setIsActive(false);
      sentinel?.release().catch(() => {});
    };
  }, [enabled]);

  return isActive;
}
